import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import {
  DEFAULT_MONTHLY_ORDERS,
  useROICalculator,
} from 'hooks/useROICalculator'
import { buildTranslate } from 'locales'
import React from 'react'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { WIZARD_QUICK_START_PATH } from '../routes'

const t = buildTranslate('recovery_ai.landing')
const tPage = buildTranslate('recovery_ai')

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

const GetStarted = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  const merchant = useCurrentMerchant()
  // This is the pre-activation pitch: it must always show an estimate, even
  // for a merchant with no self-reported order volume yet — hence the
  // DEFAULT_MONTHLY_ORDERS fallback (unlike the Details tab, which withholds
  // stats entirely without a real volume).
  const monthlyOrders = merchant?.purchase_volume ?? DEFAULT_MONTHLY_ORDERS
  const roi = useROICalculator(monthlyOrders)

  // With the fallback above, monthlyOrders is always > 0, so roi is always
  // non-null — this is a defensive narrow (never a real early-exit), not a
  // weakening of the "always render an estimate" guarantee.
  if (!roi) return null

  const { laborSavings, recoveredRevenue, totalMonthly } = roi

  return (
    <div
      className={css({
        backgroundColor: merchantTheme.colors.grey05,
        minHeight: '100vh',
      })}
    >
      <Helmet>
        <title>{tPage('page_title')}</title>
      </Helmet>

      {/* Content */}
      <div
        className={css({
          padding: '48px 86px',
        })}
      >
        <div className={`d-flex flex-column ${css({ gap: '48px' })}`}>
          {/* Hero */}
          <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
            <Text.Header3 bold>{t('hero_title')}</Text.Header3>
            <Text.BodyText3 color="grey70">
              {t('hero_description')}
            </Text.BodyText3>
          </div>

          {/* CTA Card */}
          <div
            className={css({
              backgroundColor: 'white',
              border: `1px solid ${merchantTheme.colors.grey30}`,
              borderRadius: '4px',
              padding: '24px',
            })}
          >
            <div className={`d-flex align-items-start ${css({ gap: '16px' })}`}>
              <i
                className={`fa fa-magic ${css({
                  fontSize: '24px',
                  color: merchantTheme.colors.belizeHole50,
                  flexShrink: 0,
                })}`}
                aria-hidden="true"
              />
              <div className={css({ flex: 1 })}>
                <Text.Header4>{t('cta_title')}</Text.Header4>
                <Text.BodyText4 color="grey70">
                  {t('cta_description')}
                </Text.BodyText4>
              </div>
              <Button
                kind="primary"
                onClick={() => dispatch(push(WIZARD_QUICK_START_PATH))}
              >
                {t('cta_button')}
              </Button>
            </div>
          </div>

          {/* Estimated ROI Card */}
          <div
            className={css({
              backgroundColor: 'white',
              border: `2px solid ${merchantTheme.colors.grey20}`,
              borderRadius: '4px',
              padding: '24px',
            })}
          >
            <div className={`d-flex flex-column ${css({ gap: '24px' })}`}>
              {/* ROI Header */}
              <div className={`d-flex flex-column ${css({ gap: '16px' })}`}>
                <Text.Header4>{t('roi_title')}</Text.Header4>
                <div>
                  <span
                    className={css({
                      fontSize: '32px',
                      fontWeight: 900,
                      color: merchantTheme.colors.grey90,
                      lineHeight: '40px',
                    })}
                  >
                    {formatCurrency(totalMonthly)}{' '}
                  </span>
                  <span
                    className={css({
                      fontSize: '18px',
                      fontWeight: 700,
                      color: merchantTheme.colors.grey90,
                    })}
                  >
                    {t('roi_per_month')}
                  </span>
                </div>
                <Text.BodyText3 color="grey70">
                  {t('roi_subtitle')}
                </Text.BodyText3>
              </div>

              {/* Breakdown */}
              <div
                className={css({
                  backgroundColor: merchantTheme.colors.grey05,
                  borderRadius: '4px',
                  padding: '16px 24px',
                })}
              >
                <div
                  className={css({
                    borderBottom: `1px solid ${merchantTheme.colors.grey20}`,
                  })}
                >
                  <div className="d-flex justify-content-between align-items-center py-m">
                    <div className="d-flex flex-column">
                      <Text.BodyText4 bold>
                        {t('roi_labor_savings')}
                      </Text.BodyText4>
                      <Text.BodyText4 color="grey70">
                        {t('roi_labor_formula')}
                      </Text.BodyText4>
                    </div>
                    <Text.BodyText4 bold>
                      {formatCurrency(laborSavings)}
                    </Text.BodyText4>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center py-m">
                  <div className="d-flex flex-column">
                    <Text.BodyText4 bold>
                      {t('roi_recovered_revenue')}
                    </Text.BodyText4>
                    <Text.BodyText4 color="grey70">
                      {t('roi_recovered_formula')}
                    </Text.BodyText4>
                  </div>
                  <Text.BodyText4 bold>
                    {formatCurrency(recoveredRevenue)}
                  </Text.BodyText4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GetStarted
